import React, { useState } from "react";
import Modal from "../../components/Modal";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  ManifestHeaderInterface,
  ManifestDetailInterface,
} from "../../interfaces/ManifestInterface";
import ManifestHeaderData from "./ManifestHeaderData";
import ManifestDetailsTable from "./ManifestDetailTable";
import { PrimaryButton } from "../../components/Buttons";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import DropdownMenu from "../../components/DropdownMenu";
import downloadManifest from "../../services/manifestServices";
import { useAppSelector } from "../../store/hooks";
import classNames from "classnames";
import moment from "moment";

interface ManifestModalProps {
  showPiece?: boolean;
  showShipper?: boolean;
  showConsignee?: boolean;
  showDeliveryAddress?: boolean;
  printRawExcel?: boolean;
  manifest: ManifestHeaderInterface;
  detailData: ManifestDetailInterface[];
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

enum ManifestReportOption {
  DETALLADO,
  AGRUPADO,
}
const reportOptions = [
  {
    title: "DETALLADO",
    value: ManifestReportOption.DETALLADO,
  },
  {
    title: "AGRUPADO",
    value: ManifestReportOption.AGRUPADO,
  },
];

const ManifestDetailModal: React.FC<ManifestModalProps> = ({
  showPiece = false,
  showShipper = false,
  showConsignee = false,
  showDeliveryAddress = false,
  printRawExcel = false,
  manifest,
  detailData,
  openModal,
  setOpenModal,
}) => {
  const [openPdfPopover, setOpenPdfPopover] = useState(false);
  const [openExcelPopover, setOpenExcelPopover] = useState(false);
  const user = useAppSelector((state) => state.user);

  const handleDownloadClick = async (type: number) => {
    downloadManifest(
      manifest.masterShipmentHeaderCode as string,
      type, // 1=AGRUPADO. 2=DETALLADO
      user.user?.userLogin as string,
      openPdfPopover === true ? 1 : 2 // 1=PDF. 2=EXCEL
    );
  };

  const handleDropdownMenuSelect = (
    option: { value: ManifestReportOption },
    type: "pdf" | "excel"
  ) => {
    if (type === "pdf") {
      setOpenExcelPopover(false);
      setOpenPdfPopover(true);
    } else {
      setOpenPdfPopover(false);
      setOpenExcelPopover(true);
    }
    if (option.value === ManifestReportOption.AGRUPADO) {
      handleDownloadClick(1); // 1= AGRUPADO.
    } else {
      handleDownloadClick(2); //2= DETALLADO
    }
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reporte de Envíos");

    worksheet.columns = [
      { key: "deliveryType", width: 15 },
      { key: "shipmentDate", width: 25 },
      { key: "shipmentNumber", width: 15 },
      { key: "buCodeSource", width: 15 },
      { key: "locationOriginCode", width: 25 },
      { key: "buCodeDestination", width: 15 },
      { key: "locationDestinationCode", width: 15 },
      { key: "serviceName", width: 10 },
      { key: "paymentModeName", width: 20 },
      { key: "physicalWeight", width: 20 },
      { key: "totalPieces", width: 15 },
      { key: "manifestPieces", width: 20 },
      { key: "shipperName", width: 25 },
      { key: "shipperPhone", width: 25 },
      { key: "shipperIdentification", width: 25 },
      { key: "consigneeName", width: 25 },
      { key: "consigneePhone", width: 25 },
      { key: "consigneeIdentification", width: 25 },
      { key: "consigneeAddress", width: 25 },
    ];

    // Manifest
    const row2 = worksheet.getRow(2);
    row2.getCell(
      1
    ).value = `Manifiesto de Despacho en Tránsito ${manifest.masterShipmentHeaderCode}`;

    // Business unit
    const row3 = worksheet.getRow(3);
    row3.getCell(
      1
    ).value = `Tienda ${user.businessUnit?.code} - ${user.businessUnit?.name}`;

    // User
    const row4 = worksheet.getRow(4);
    row4.getCell(1).value = `Elaborado por ${user.user?.userName} ${
      user.user?.userLastName
    } (${new Date().toLocaleString()})`;

    // Totals
    const filteredData = detailData.filter((d) => d.statusID === 31);
    row2.getCell(5).value = "Total Guías";
    row3.getCell(5).value = "Total Piezas";
    row4.getCell(5).value = "Total Peso Físico (kg)";
    row2.getCell(6).value = filteredData.length;
    row3.getCell(6).value = filteredData.reduce(
      (acc, item) => acc + item.totalPieces,
      0
    );
    row4.getCell(6).value = +filteredData
      .reduce((acc, item) => acc + (item.physicalWeight ?? 0), 0)
      .toFixed(2);

    // Header
    const headerRow = worksheet.getRow(7);
    headerRow.values = [
      "Retiro",
      "Fecha Envío",
      "Guía",
      "Tienda Origen",
      "Ciudad Origen",
      "Tienda Destino",
      "Ciudad Destino",
      "Servicio",
      "Modalidad de Pago",
      "Peso Físico (kg)",
      "Total Piezas Guía",
      "Piezas en Manifiesto",
      "Remitente",
      "Teléfono del Remitente",
      "Cédula/RIF del Remitente",
      "Destinatario",
      "Teléfono del Destinatario",
      "Cédula/RIF del Destinatario",
      "Dirección de Entrega",
    ];
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.border = {
        bottom: { style: "thin" },
      };
    });

    // Data
    detailData
      .filter((d) => d.statusID === 31)
      .forEach((item) => {
        const row = worksheet.addRow({
          deliveryType: item.isRetiraOficina ? "Oficina" : "Domicilio",
          shipmentDate: moment(item.creationDateShipment).format(
            "DD/MM/YYYY hh:mm:ss a"
          ),
          shipmentNumber: item.shipmentNumber,
          buCodeSource: item.buCodeSource,
          locationOriginCode: item.locationOriginCode,
          buCodeDestination: item.buCodeDestination,
          locationDestinationCode: item.locationDestinationCode,
          serviceName: item.serviceName,
          paymentModeName: item.paymentModeName,
          physicalWeight: item.physicalWeight?.toFixed(2),
          totalPieces: item.pieceNumber,
          manifestPieces: item.totalPieces,
          shipperName: item.shipperName,
          shipperPhone: item.shipperPhone,
          shipperIdentification: item.shipperIdentification,
          consigneeName: item.consigneeName,
          consigneePhone: item.consigneePhone,
          consigneeIdentification: item.consigneeIdentification,
          consigneeAddress: item.isRetiraOficina
            ? "En Tienda"
            : item.locationName,
        });

        row.getCell(6).numFmt = "0.00";
      });

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "ReporteEnvios.xlsx");
  };

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="bg-gray-200"
    >
      <div className="p-4 flex flex-1 flex-col min-w-[60rem] mb-4">
        {/* Header Title */}
        <div className="flex justify-between">
          <div>
            <span className="font-bold text-xl">Manifiesto: </span>
            <span className="text-indigo-600 font-bold text-xl">
              {manifest.masterShipmentHeaderCode}
            </span>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="flex items-center justify-center relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={() => setOpenModal(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Cerrar panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="w-full mb-1">
          <span className="text-gray-600">Fecha: </span>
          <span className="text-gray-800">
            {!!manifest?.manifestClosedDate &&
              new Date(manifest.manifestClosedDate)
                .toLocaleDateString("es-VE", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace("a", "AM")
                .replace("p", "PM")
                .slice(0, -4)}
          </span>
        </div>
      </div>

      <hr className="my-1 mx-2 mb-2" />

      {/* Header data */}
      <div className="flex flex-1 gap-4">
        {/* Shipper */}
        <ManifestHeaderData
          masterShipmentHeaderID={manifest.masterShipmentHeaderID}
          masterShipmentHeaderCode={manifest.masterShipmentHeaderCode}
          manifestTypeID={manifest.manifestTypeID}
          buCodeSource={manifest.buCodeSource}
          buCodeDestination={manifest.buCodeDestination}
          route={manifest.route?.toUpperCase()}
          creationDate={manifest.creationDate}
          transport={
            manifest.transportModelName?.toUpperCase() +
            " - " +
            manifest.transportPlate?.toUpperCase()
          }
          driver1Name={manifest.driver1Name?.toUpperCase()}
          statusName={manifest?.statusName?.toUpperCase() || ""}
        />
      </div>

      {/* Línea separadora */}
      <hr className="my-2 mx-2" />

      {/* Detalles del manifiesto */}
      <div className="gap-4 p-2">
        <ManifestDetailsTable
          showPiece={showPiece}
          showShipper={showShipper}
          showConsignee={showConsignee}
          showDeliveryAddress={showDeliveryAddress}
          data={detailData}
          showItem={false}
          showAllStatus={true}
        />
      </div>

      <div
        className={classNames(
          "flex flex-1 flex-row mt-8",
          printRawExcel && "hidden"
        )}
      >
        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenPdfPopover(!openPdfPopover)}
            className="relative w-28"
          >
            PDF
            {openPdfPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openPdfPopover}
            setOpen={setOpenPdfPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "pdf")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />
        </div>

        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenExcelPopover(!openExcelPopover)}
            className="relative w-28"
          >
            EXCEL
            {openExcelPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openExcelPopover}
            setOpen={setOpenExcelPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "excel")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />
        </div>
      </div>

      <div
        className={classNames(
          "flex flex-1 flex-row mt-8",
          !printRawExcel && "hidden"
        )}
      >
        <PrimaryButton className="w-44" onClick={exportToExcel}>
          Descargar Reporte
        </PrimaryButton>
      </div>
    </Modal>
  );
};
export default ManifestDetailModal;
